import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../../Api";
import "./dashboardCalendar.style.css";

function DashboardCalendar({ projects }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [dateAssignments, setDateAssignments] = useState([]);
    const [loadingAssignments, setLoadingAssignments] = useState(false);
    const [expandedProjects, setExpandedProjects] = useState(new Set());

    // Toggle project expansion
    const toggleProjectExpansion = (projectId) => {
        setExpandedProjects(prev => {
            const newSet = new Set(prev);
            if (newSet.has(projectId)) {
                newSet.delete(projectId);
            } else {
                newSet.add(projectId);
            }
            return newSet;
        });
    };

    // Fetch assignments for selected date
    useEffect(() => {
        const fetchAssignmentsForDate = async () => {
            if (!selectedDate) return;

            setLoadingAssignments(true);
            try {
                const dateStr = selectedDate.toISOString().split('T')[0];
                const response = await api.get(`/project-user-assignments`, {
                    params: {
                        start_date: dateStr,
                        end_date: dateStr
                    }
                });
                setDateAssignments(response.data || []);
            } catch (error) {
                console.error('Error fetching assignments:', error);
                setDateAssignments([]);
            } finally {
                setLoadingAssignments(false);
            }
        };

        fetchAssignmentsForDate();
    }, [selectedDate]);

    // Generate consistent color for project
    const getProjectColor = (projectId) => {
        const colors = [
            '#6B8CAE', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
            '#EC4899', '#14B8A6', '#F97316', '#3B82F6', '#84CC16'
        ];
        return colors[projectId % colors.length];
    };

    // Get calendar days for current month (compact - 2 weeks)
    const getCalendarDays = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const days = [];
        // Show 14 days starting from today
        for (let i = 0; i < 14; i++) {
            const date = new Date(today);
            date.setDate(today.getDate() + i);
            days.push(date);
        }
        return days;
    };

    // Get projects for a specific date
    const getProjectsForDate = (date) => {
        return projects.filter(project => {
            if (!project.start_date || !project.end_date) return false;

            const startDate = new Date(project.start_date);
            const endDate = new Date(project.end_date);
            const checkDate = new Date(date);

            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(23, 59, 59, 999);
            checkDate.setHours(0, 0, 0, 0);

            return checkDate >= startDate && checkDate <= endDate;
        });
    };

    // Get assignments for a specific project on selected date
    const getAssignmentsForProject = (projectId) => {
        return dateAssignments.filter(assignment => assignment.project_id === projectId);
    };

    // Handle day click
    const handleDayClick = (day) => {
        const newDate = new Date(day);
        newDate.setHours(0, 0, 0, 0);
        setSelectedDate(newDate);
    };

    const calendarDays = getCalendarDays();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDayProjects = getProjectsForDate(selectedDate);

    return (
        <section className="dashboard-calendar-widget card" aria-labelledby="calendar-widget-title">
            <header className="dashboard-calendar-header card-header">
                <h3 id="calendar-widget-title" className="card-title">Aankomende Planning</h3>
                <button
                    type="button"
                    className="btn btn-view"
                    onClick={() => navigate('/agenda')}
                    aria-label="Bekijk volledige agenda"
                >
                    Volledige agenda <span aria-hidden="true">→</span>
                </button>
            </header>

            <div className="dashboard-calendar-grid card-body" role="list" aria-label="Komende 14 dagen">
                {calendarDays.map((day, index) => {
                    const dayProjects = getProjectsForDate(day);
                    const isToday = day.getTime() === today.getTime();
                    const isSelected = day.getTime() === selectedDate.getTime();

                    return (
                        <button
                            key={index}
                            type="button"
                            className={`dashboard-calendar-day ${isToday ? "today" : ""} ${isSelected ? "selected" : ""}`}
                            onClick={() => handleDayClick(day)}
                            aria-label={`${day.toLocaleDateString("nl-NL", { 
                                weekday: 'long', 
                                day: 'numeric', 
                                month: 'long' 
                            })}${dayProjects.length > 0 ? `, ${dayProjects.length} project${dayProjects.length > 1 ? 'en' : ''}` : ''}`}
                            aria-current={isSelected ? "date" : undefined}
                        >
                            <div className="dashboard-day-header">
                                <div className="dashboard-day-name text-sm">
                                    {day.toLocaleDateString("nl-NL", { weekday: "short" })}
                                </div>
                                <div className="dashboard-day-number font-semibold">
                                    {day.getDate()}
                                </div>
                            </div>

                            {dayProjects.length > 0 ? (
                                <div className="dashboard-day-projects" aria-hidden="true">
                                    {dayProjects.slice(0, 2).map(project => (
                                        <div
                                            key={project.id}
                                            className="dashboard-project-dot"
                                            style={{ '--project-color': getProjectColor(project.id) }}
                                            title={project.name}
                                            role="presentation"
                                        />
                                    ))}
                                    {dayProjects.length > 2 && (
                                        <div className="dashboard-more-indicator text-xs">
                                            +{dayProjects.length - 2}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="dashboard-day-empty text-muted" aria-hidden="true">-</div>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Selected Date Projects with Assigned Users */}
            <section className="selected-date-section" aria-labelledby="selected-date-heading">
                <h4 id="selected-date-heading" className="text-lg font-semibold mb-4">
                    <time dateTime={selectedDate.toISOString()}>
                        {selectedDate.toLocaleDateString('nl-NL', {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                        })}
                    </time>
                </h4>

                {loadingAssignments ? (
                    <div className="loading-assignments p-4 text-center" role="status" aria-live="polite">
                        {t('common.loading')}
                    </div>
                ) : selectedDayProjects.length === 0 ? (
                    <div className="no-projects-message p-4 text-center text-muted" role="status">
                        {t('dashboard.noProjectsScheduled')}
                    </div>
                ) : (() => {
                    const projectsWithAssignments = selectedDayProjects.filter(
                        project => getAssignmentsForProject(project.id).length > 0
                    );

                    if (projectsWithAssignments.length === 0) {
                        return (
                            <div className="no-projects-message p-4 text-center" role="status">
                                <p className="mb-2">
                                    {t('dashboard.projectsButNoAssignments', { count: selectedDayProjects.length })}
                                </p>
                                <small className="text-sm text-muted">
                                    {t('dashboard.goToAgendaToAssign')}
                                </small>
                            </div>
                        );
                    }

                    return (
                        <div className="selected-day-projects-list">
                            {projectsWithAssignments.map(project => {
                                const projectAssignments = getAssignmentsForProject(project.id);
                                const isExpanded = expandedProjects.has(project.id);
                                const maxVisible = 3;
                                const hasMore = projectAssignments.length > maxVisible;
                                const visibleAssignments = isExpanded ? projectAssignments : projectAssignments.slice(0, maxVisible);

                                return (
                                    <article key={project.id} className="selected-project-card card">
                                        <header className="selected-project-header">
                                            <div
                                                className="selected-project-color"
                                                style={{ backgroundColor: getProjectColor(project.id) }}
                                                aria-hidden="true"
                                            />
                                            <div className="selected-project-info">
                                                <button
                                                    type="button"
                                                    className="selected-project-name btn-link text-left"
                                                    onClick={() => navigate(`/projects/${project.id}`)}
                                                    aria-label={`Bekijk project ${project.name}`}
                                                >
                                                    {project.name}
                                                </button>
                                                <div className="selected-project-client text-sm text-muted">
                                                    {project.client}
                                                </div>
                                            </div>
                                        </header>

                                        {/* Assigned Users */}
                                        {projectAssignments.length > 0 ? (
                                            <div className="project-assigned-users">
                                                <div className="assigned-users-label text-sm font-semibold mb-2">
                                                    <span aria-hidden="true">👷</span> Ingepland ({projectAssignments.length})
                                                </div>
                                                <ul className="assigned-users-list">
                                                    {visibleAssignments.map(assignment => (
                                                        <li key={assignment.id}>
                                                            <button
                                                                type="button"
                                                                className="assigned-user-item"
                                                                onClick={() => navigate(`/users/${assignment.user_id}`)}
                                                                aria-label={`Bekijk gebruiker ${assignment.user_name}`}
                                                            >
                                                                <div className="assigned-user-avatar" aria-hidden="true">
                                                                    {assignment.user_name ? assignment.user_name.charAt(0).toUpperCase() : '?'}
                                                                </div>
                                                                <div className="assigned-user-details">
                                                                    <div className="assigned-user-name">{assignment.user_name}</div>
                                                                    {assignment.project_role && (
                                                                        <div className="assigned-user-role text-sm text-muted">
                                                                            {assignment.project_role}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </button>
                                                        </li>
                                                    ))}
                                                </ul>
                                                {hasMore && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary show-more-button"
                                                        onClick={() => toggleProjectExpansion(project.id)}
                                                        aria-expanded={isExpanded}
                                                        aria-label={isExpanded ? 'Toon minder' : `Toon ${projectAssignments.length - maxVisible} meer`}
                                                    >
                                                        {isExpanded ? (
                                                            <><span aria-hidden="true">▲</span> {t('common.showLess')}</>
                                                        ) : (
                                                            <><span aria-hidden="true">▼</span> {t('common.showMore', { count: projectAssignments.length - maxVisible })}</>
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="no-assignments-message p-3 text-muted text-sm">
                                                {t('agenda.noAssignedEmployees')}
                                            </div>
                                        )}
                                    </article>
                                );
                            })}
                        </div>
                    );
                })()}
            </section>

            {/* Active Projects Summary - Kept for backward compatibility */}
            <section className="dashboard-active-projects" aria-labelledby="active-projects-heading">
                <h4 id="active-projects-heading" className="text-lg font-semibold mb-3">
                    Actieve Projecten
                </h4>
                <ul className="active-projects-list">
                    {projects
                        .filter(p => {
                            if (!p.start_date || !p.end_date) return false;
                            const start = new Date(p.start_date);
                            const end = new Date(p.end_date);
                            return start <= today && end >= today;
                        })
                        .slice(0, 5)
                        .map(project => (
                            <li key={project.id}>
                                <button
                                    type="button"
                                    className="active-project-item"
                                    onClick={() => navigate(`/projects/${project.id}`)}
                                    aria-label={`Bekijk project ${project.name}`}
                                >
                                    <div
                                        className="active-project-color"
                                        style={{ backgroundColor: getProjectColor(project.id) }}
                                        aria-hidden="true"
                                    />
                                    <div className="active-project-info">
                                        <div className="active-project-name font-medium">
                                            {project.name}
                                        </div>
                                        <div className="active-project-client text-sm text-muted">
                                            {project.client}
                                        </div>
                                    </div>
                                </button>
                            </li>
                        ))}
                </ul>
            </section>
        </section>
    );
}

DashboardCalendar.propTypes = {
    projects: PropTypes.array.isRequired
};

export default DashboardCalendar;

